import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import ReactDOM from 'react-dom';
import axios from 'axios';
import { motion } from 'framer-motion';
import {
  FiSearch, FiFileText, FiClock, FiMessageSquare, FiRefreshCw,
  FiThumbsUp, FiThumbsDown,
} from 'react-icons/fi';
import '../../styles/QuoteProposal.css';
import '../../styles/Voice.css';
import '../commissionscan/CommissionScan.css';
import '../../styles/DriverLicenseScanHistory.css';
import PreviewIcon from '../../assets/images/ViewDetailsIcon.svg';
import DriverLicenseScanPreview, { DLTransaction } from './DriverLicenseScanPreview';
import { useToast } from '../../context/ToastContext';

/* ── Status config ── */
const STATUS_CONFIG: Record<string, { label: string; color: string }> = {
  Success: { label: 'Success', color: '#34d399' },
  Failed:  { label: 'Failed',  color: '#f87171' },
};

const PAGE_SIZE      = 10;
const getToolboxToken  = () => localStorage.getItem('toolboxToken') || '';
const getNcToken   = () => localStorage.getItem('amsToken') || '';

const formatDate = (d: string): string => {
  if (!d) return '—';
  try {
    const dt = new Date(d);
    const m = ['Jan','Feb','Mar','Apr','May','Jun','Jul','Aug','Sep','Oct','Nov','Dec'];
    return `${m[dt.getMonth()]} ${dt.getDate()}, ${dt.getFullYear()}`;
  } catch { return d; }
};

/* ── Types ── */
interface NowCertsClient {
  name: string;
  databaseId: string;
  eMail?: string;
}

const getClientName = (tx: DLTransaction): string => {
  try {
    if (!tx.selectionOptionsJson) return '—';
    const parsed = JSON.parse(tx.selectionOptionsJson);
    return parsed.clientName || '—';
  } catch { return '—'; }
};


/* ════════════════════════════════════════════════════════════ */
const DriverLicenseScanHistory: React.FC = () => {
  const [rows,       setRows]       = useState<DLTransaction[]>([]);
  const [loading,    setLoading]    = useState(false);
  const [error,      setError]      = useState<string | null>(null);
  const [search,     setSearch]     = useState('');
  const [page,       setPage]       = useState(1);
  const [totalCount, setTotalCount] = useState(0);

  /* Preview */
  const [previewTx, setPreviewTx] = useState<DLTransaction | null>(null);

  /* Upload to AMS */
  const [uploadAmsOpen,           setUploadAmsOpen]           = useState(false);
  const [uploadAmsTxId,           setUploadAmsTxId]           = useState('');
  const [uploadAmsFileName,       setUploadAmsFileName]       = useState('');
  const [uploadAmsClientQuery,    setUploadAmsClientQuery]    = useState('');
  const [uploadAmsClientList,     setUploadAmsClientList]     = useState<NowCertsClient[]>([]);
  const [uploadAmsClientLoading,  setUploadAmsClientLoading]  = useState(false);
  const [uploadAmsSelectedClient, setUploadAmsSelectedClient] = useState<NowCertsClient | null>(null);
  const [uploadAmsLoading,        setUploadAmsLoading]        = useState(false);
  const [uploadAmsError,          setUploadAmsError]          = useState('');
  const [uploadAmsSuccess,        setUploadAmsSuccess]        = useState(false);

  const { showSuccess } = useToast();

  /* Feedback */
  const [feedbackTxId,       setFeedbackTxId]       = useState<string | null>(null);
  const [feedbackText,       setFeedbackText]        = useState('');
  const [feedbackRating,     setFeedbackRating]      = useState<boolean | null>(null);
  const [feedbackAnchor,     setFeedbackAnchor]      = useState<{ top: number; right: number } | null>(null);
  const [feedbackSubmitting, setFeedbackSubmitting]  = useState(false);
  const [feedBackError,      setFeedBackError]       = useState('');
  const feedbackRef = useRef<HTMLDivElement>(null);

  /* ── Fetch history ── */
  const fetchData = useCallback(async (pageNum: number, signal?: AbortSignal) => {
    setLoading(true);
    setError(null);
    try {
      const res = await fetch(
        `${(process.env.REACT_APP_DataFabric_Url    || '')}/api/NCAIDriverLicenseScan/history?pageNumber=${pageNum}&recordCount=${PAGE_SIZE}`,
        {
          headers: { Authorization: `Bearer ${getToolboxToken()}`, accept: 'application/json' },
          signal,
        },
      );
      if (!res.ok) throw new Error(`Failed to load history (${res.status})`);
      const data: DLTransaction[] = await res.json();
      setRows(Array.isArray(data) ? data : []);
      setTotalCount(
        Array.isArray(data) && data.length > 0
          ? (data[0].totalCount ?? data.length)
          : 0,
      );
    } catch (e: any) {
      if (e.name === 'AbortError') return;
      setError(e.message || 'Failed to load history');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    let controller: AbortController | undefined;
    const timer = setTimeout(() => {
      controller = new AbortController();
      fetchData(page, controller.signal);
    }, 0);
    return () => { clearTimeout(timer); controller?.abort(); };
  }, [fetchData, page]);

  const filtered = useMemo(() => {
    if (!search.trim()) return rows;
    const q = search.toLowerCase();
    return rows.filter(r =>
      r.fileName?.toLowerCase().includes(q) ||
      r.userName?.toLowerCase().includes(q) ||
      getClientName(r).toLowerCase().includes(q),
    );
  }, [rows, search]);

  useEffect(() => { setPage(1); }, [search]);

  const totalPages = Math.max(1, Math.ceil(totalCount / PAGE_SIZE));
  const pageList = Array.from({ length: totalPages }, (_, i) => i + 1)
    .filter(p => p === 1 || p === totalPages || Math.abs(p - page) <= 1)
    .reduce<(number | '…')[]>((acc, p, idx, arr) => {
      if (idx > 0 && (p as number) - (arr[idx - 1] as number) > 1) acc.push('…');
      acc.push(p);
      return acc;
    }, []);

  /* ── Preview ── */
  const onPreviewClick = useCallback((tx: DLTransaction) => {
    setPreviewTx(tx);
  }, []);

  /* ── Upload to AMS ── */
  const openUploadAms = useCallback((tx: DLTransaction) => {
    setUploadAmsTxId(tx.transactionId);
    setUploadAmsFileName(tx.fileName);
    setUploadAmsClientQuery('');
    setUploadAmsClientList([]);
    setUploadAmsSelectedClient(null);
    setUploadAmsError('');
    setUploadAmsSuccess(false);
    setUploadAmsOpen(true);
  }, []);

  const getAmsClients = useCallback((query: string) => {
    if (!query.trim()) { setUploadAmsClientList([]); return; }
    setUploadAmsClientLoading(true);
    axios.get(`${(process.env.REACT_APP_NOWCERTSAPI        || '')}/api/nowcert/customerlist`, {
      params: { searchText: query, nowCertsToken: getNcToken() },
      headers: { Authorization: `Bearer ${getToolboxToken()}` },
    })
      .then(r => { setUploadAmsClientList(r.data || []); setUploadAmsClientLoading(false); })
      .catch(() => setUploadAmsClientLoading(false));
  }, []);

  useEffect(() => {
    const t = setTimeout(() => getAmsClients(uploadAmsClientQuery), 400);
    return () => clearTimeout(t);
  }, [uploadAmsClientQuery, getAmsClients]);

  const onUploadToAMSConfirmed = useCallback(async () => {
    if (!uploadAmsSelectedClient) return;
    setUploadAmsLoading(true);
    setUploadAmsError('');
    try {
      const res = await axios.post(
        `${(process.env.REACT_APP_NOWCERTSAPI        || '')}/api/nowcert/uploadV1`,
        {
          TransactionId:  uploadAmsTxId,
          FileName:       uploadAmsFileName,
          NCPolicyId:     '',
          NCInsuredId:    uploadAmsSelectedClient.databaseId,
          FileData:       '',
          FolderName:     'Driver License',
          TargetName:     'DriverLicenseScan',
          NowCertsToken:  getNcToken(),
        },
        { headers: { Authorization: `Bearer ${getToolboxToken()}` } },
      );
      setUploadAmsLoading(false);
      if (res.data.success) {
        setUploadAmsSuccess(true);
        setTimeout(() => { setUploadAmsOpen(false); setUploadAmsSuccess(false); }, 1500);
      } else {
        setUploadAmsError(res.data.errorMessage || 'Upload failed.');
      }
    } catch (err: any) {
      setUploadAmsLoading(false);
      setUploadAmsError('Error while uploading to AMS.');
    }
  }, [uploadAmsTxId, uploadAmsFileName, uploadAmsSelectedClient]);

  /* ── Feedback ── */
  const openFeedback = useCallback((tx: DLTransaction, e: React.MouseEvent<HTMLButtonElement>) => {
    const rect = e.currentTarget.getBoundingClientRect();
    setFeedbackTxId(tx.transactionId);
    setFeedbackText('');
    setFeedbackRating(tx.isLiked ?? null);
    setFeedBackError('');
    setFeedbackAnchor({ top: rect.bottom + 8, right: window.innerWidth - rect.right });
  }, []);

  const closeFeedback = useCallback(() => {
    setFeedbackTxId(null);
    setFeedbackAnchor(null);
    setFeedbackText('');
    setFeedbackRating(null);
    setFeedBackError('');
  }, []);

  const saveRating = useCallback((txId: string, isLiked: boolean | null, feedback: string) => {
    axios.post(
      `${(process.env.REACT_APP_DataFabric_Url    || '')}/api/NCAICommissionStatement/feedback`,
      { Target: 2, TransactionId: txId, IsLiked: isLiked, Feedback: feedback ?? '' },
      { headers: { Authorization: `Bearer ${getToolboxToken()}` } },
    )
      .then(() => { showSuccess('Feedback submitted successfully!'); closeFeedback(); })
      .catch(() => { /* silent */ });
  }, [closeFeedback]);

  const onThumbsUpClick = useCallback(() => {
    if (!feedbackTxId) return;
    const cur = rows.find(r => r.transactionId === feedbackTxId);
    const next: boolean | null = cur?.isLiked === true ? null : true;
    setRows(prev => prev.map(r => r.transactionId === feedbackTxId ? { ...r, isLiked: next } : r));
    closeFeedback();
    saveRating(feedbackTxId, next, feedbackText);
  }, [feedbackTxId, rows, feedbackText, saveRating, closeFeedback]);

  const onThumbsDownClick = useCallback(() => {
    if (!feedbackTxId) return;
    const cur = rows.find(r => r.transactionId === feedbackTxId);
    const next: boolean | null = cur?.isLiked === false ? null : false;
    setRows(prev => prev.map(r => r.transactionId === feedbackTxId ? { ...r, isLiked: next } : r));
    closeFeedback();
    saveRating(feedbackTxId, next, feedbackText);
  }, [feedbackTxId, rows, feedbackText, saveRating, closeFeedback]);

  const onSubmitFeedbackClick = useCallback(() => {
    if (!feedbackTxId) return;
    if (!feedbackText.trim()) { setFeedBackError('Please enter your feedback'); return; }
    if (feedbackText.length > 250) { setFeedBackError('Please provide your feedback within 250 characters'); return; }
    setFeedBackError('');
    setFeedbackSubmitting(true);
    axios.post(
      `${(process.env.REACT_APP_DataFabric_Url    || '')}/api/NCAICommissionStatement/feedback`,
      { Target: 2, TransactionId: feedbackTxId, IsLiked: feedbackRating, Feedback: feedbackText },
      { headers: { Authorization: `Bearer ${getToolboxToken()}` } },
    )
      .then(() => { setFeedbackSubmitting(false); showSuccess('Feedback submitted successfully!'); closeFeedback(); })
      .catch(() => { setFeedbackSubmitting(false); });
  }, [feedbackTxId, feedbackText, feedbackRating, closeFeedback]);

  useEffect(() => {
    if (!feedbackTxId) return;
    const handler = (e: MouseEvent) => {
      if (feedbackRef.current && !feedbackRef.current.contains(e.target as Node)) closeFeedback();
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [feedbackTxId, closeFeedback]);


  /* ════════════════════ RENDER ════════════════════ */
  return (
    <>
      <motion.div
        className="qp-history"
        initial={{ opacity: 0, y: 10 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.25 }}
      >
        {/* Header */}
        <div className="qp-history__header">
          <h3 className="qp-history__title driver-license-scan-history__title-flush">Driver License Scan History</h3>
          <div className="driver-license-scan-history__header-actions">
            <div className="qp-history__search">
              <FiSearch size={13} />
              <input
                type="text"
                placeholder="Search…"
                value={search}
                onChange={e => setSearch(e.target.value)}
              />
            </div>
            <motion.button
              className="csh-refresh-btn"
              onClick={() => fetchData(page)}
              whileHover={{ scale: 1.03 }}
              whileTap={{ scale: 0.97 }}
            >
              <FiRefreshCw size={13} className={loading && rows.length > 0 ? 'csh-spin' : ''} />
              Refresh
            </motion.button>
          </div>
        </div>

        {/* Body */}
        <div className="qp-history__body">
          {error ? (
            <div className="qp-empty-state driver-license-scan-history__error-state">
              <FiFileText size={28} />
              <p>{error}</p>
            </div>
          ) : loading ? (
            <div className="qp-history-loader driver-license-scan-history__loader">
              <div className="qp-history-loader__orb">
                <div className="qp-history-loader__ring qp-history-loader__ring--1" />
                <div className="qp-history-loader__ring qp-history-loader__ring--2" />
                <div className="qp-history-loader__core"><FiClock size={16} /></div>
              </div>
              <div className="qp-history-loader__text">
                <p className="qp-history-loader__title">Loading History</p>
                <p className="qp-history-loader__sub">
                  Fetching driver license scan records
                  <span className="qp-init-loader__dots"><span>.</span><span>.</span><span>.</span></span>
                </p>
              </div>
            </div>
          ) : filtered.length === 0 ? (
            <div className="qp-empty-state">
              <FiFileText size={28} />
              <p>{search ? 'No records match your search' : 'No driver license scans yet.'}</p>
            </div>
          ) : (
            <div className="qp-table-wrap glass-surface">
              <table className="qp-table">
                <thead>
                  <tr>
                    <th className="qp-table__th">File Name</th>
                    <th className="qp-table__th">Date</th>
                    <th className="qp-table__th">User</th>
                    <th className="qp-table__th">Client Name</th>
                    <th className="qp-table__th">Status</th>
                    <th className="qp-table__th driver-license-scan-history__center">Updated In AMS</th>
                    <th className="qp-table__th driver-license-scan-history__center">Action</th>
                    <th className="qp-table__th driver-license-scan-history__center">Feedback</th>
                  </tr>
                </thead>
                <tbody>
                  {filtered.map((tx, i) => {
                    const isSuccess = tx.status === 'Success';
                    return (
                      <tr key={tx.transactionId ?? i} className="qp-table__row">
                        <td
                          className="qp-table__td driver-license-scan-history__filename-cell"
                        >
                          {tx.fileName}
                        </td>
                        <td className="qp-table__td driver-license-scan-history__nowrap">
                          {formatDate(tx.created ?? '')}
                        </td>
                        <td className="qp-table__td">{tx.userName}</td>
                        <td className="qp-table__td">{getClientName(tx)}</td>
                        <td className="qp-table__td">
                          <span
                            className="qp-status-pill"
                            style={{ color: (STATUS_CONFIG[tx.status] || STATUS_CONFIG.Failed).color }}
                          >
                            {(STATUS_CONFIG[tx.status] || { label: tx.status }).label}
                          </span>
                        </td>
                        <td className="qp-table__td driver-license-scan-history__center">
                          <span
                            className="driver-license-scan-history__ams-badge"
                            style={{
                              background: tx.isUpdateInNowcerts ? '#d1fae5' : '#fee2e2',
                              color:      tx.isUpdateInNowcerts ? '#065f46' : '#991b1b',
                            }}
                          >
                            {tx.isUpdateInNowcerts ? 'Yes' : 'No'}
                          </span>
                        </td>
                        <td className="qp-table__td driver-license-scan-history__center">
                          <div className="qp-table__actions">
                            {isSuccess && (
                              <>
                                <button
                                  className="qp-table__action-btn"
                                  title="Preview"
                                  onClick={() => onPreviewClick(tx)}
                                >
                                  <img src={PreviewIcon} alt="Preview" width={30} height={30} />
                                </button>
                              </>
                            )}
                            {!isSuccess && (
                              <span className="driver-license-scan-history__not-available">
                                Not Available
                              </span>
                            )}
                          </div>
                        </td>
                        <td className="qp-table__td driver-license-scan-history__center">
                          <button
                            className="qp-table__action-btn driver-license-scan-history__feedback-btn"
                            title="Feedback"
                            onClick={e => openFeedback(tx, e)}
                          >
                            <FiMessageSquare size={14} />
                          </button>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}

          {/* Pagination */}
          {totalCount > 0 && (
            <div className="qp-pagination">
              <span className="qp-pagination__info">
                {(page - 1) * PAGE_SIZE + 1}–{Math.min(page * PAGE_SIZE, totalCount)} of {totalCount}
              </span>
              <div className="qp-pagination__controls">
                <button className="qp-pagination__btn" disabled={page === 1} onClick={() => setPage(1)} title="First">«</button>
                <button className="qp-pagination__btn" disabled={page === 1} onClick={() => setPage(p => p - 1)} title="Previous">‹</button>
                {pageList.map((p, i) =>
                  p === '…'
                    ? <span key={`ellipsis-${i}`} className="qp-pagination__ellipsis">…</span>
                    : <button
                        key={p}
                        className={`qp-pagination__btn${page === p ? ' qp-pagination__btn--active' : ''}`}
                        onClick={() => setPage(p as number)}
                      >{p}</button>
                )}
                <button className="qp-pagination__btn" disabled={page === totalPages} onClick={() => setPage(p => p + 1)} title="Next">›</button>
                <button className="qp-pagination__btn" disabled={page === totalPages} onClick={() => setPage(totalPages)} title="Last">»</button>
              </div>
            </div>
          )}
        </div>
      </motion.div>

      {/* ── Feedback Popup ── */}
      {feedbackTxId && feedbackAnchor && (
        <div
          ref={feedbackRef}
          className="csfb-popup"
          style={{ top: feedbackAnchor.top, right: feedbackAnchor.right }}
        >
          <h4 className="csfb-title">Feedback</h4>
          <textarea
            className="csfb-textarea"
            placeholder="Enter your feedback here"
            value={feedbackText}
            onChange={e => setFeedbackText(e.target.value)}
            rows={4}
          />
          <div className="csfb-rating">
            <button
              className={`csfb-rating-btn${feedbackRating === true ? ' csfb-rating-btn--up' : ''}`}
              onClick={onThumbsUpClick}
              title="Thumbs up"
            >
              <FiThumbsUp size={20} />
            </button>
            <button
              className={`csfb-rating-btn${feedbackRating === false ? ' csfb-rating-btn--down' : ''}`}
              onClick={onThumbsDownClick}
              title="Thumbs down"
            >
              <FiThumbsDown size={20} />
            </button>
          </div>
          {feedBackError && <p className="csfb-error">{feedBackError}</p>}
          <div className="csfb-actions">
            <button className="csfb-btn-cancel" onClick={closeFeedback}>Cancel</button>
            <button
              className="csfb-btn-submit"
              disabled={feedbackSubmitting}
              onClick={onSubmitFeedbackClick}
            >
              {feedbackSubmitting ? 'Submitting…' : 'Submit'}
            </button>
          </div>
        </div>
      )}

      {/* ── Upload to AMS Dialog ── */}
      {uploadAmsOpen && ReactDOM.createPortal(
        <div className="csams-overlay" onClick={() => setUploadAmsOpen(false)}>
          <div className="csams-modal" onClick={e => e.stopPropagation()}>
            <div className="csams-modal__header">
              <h3 className="csams-modal__title">Upload to AMS</h3>
              <button className="csams-modal__close" onClick={() => setUploadAmsOpen(false)}>✕</button>
            </div>
            <div className="csams-modal__body">
              <label className="csams-label">Search Client</label>
              <div className="csams-search-wrap">
                <input
                  className="csams-search-input"
                  placeholder="Enter client name..."
                  value={uploadAmsClientQuery}
                  onChange={e => { setUploadAmsClientQuery(e.target.value); setUploadAmsSelectedClient(null); }}
                  autoFocus
                />
                {uploadAmsClientLoading && <span className="csams-search-spinner" />}
                {uploadAmsClientList.length > 0 && !uploadAmsSelectedClient && (
                  <ul className="csams-dropdown">
                    {uploadAmsClientList.map(c => (
                      <li
                        key={c.databaseId}
                        className="csams-dropdown__item"
                        onClick={() => {
                          setUploadAmsSelectedClient(c);
                          setUploadAmsClientQuery(c.name);
                          setUploadAmsClientList([]);
                        }}
                      >
                        {c.name}
                        {c.eMail && <span className="csams-dropdown__email">{c.eMail}</span>}
                      </li>
                    ))}
                  </ul>
                )}
              </div>
              {uploadAmsSuccess && <p className="csams-success">Driver License uploaded to AMS successfully!</p>}
              {uploadAmsError   && <p className="csams-error">{uploadAmsError}</p>}
            </div>
            <div className="csams-modal__footer">
              <button className="csams-btn csams-btn--cancel" onClick={() => setUploadAmsOpen(false)}>Cancel</button>
              <button
                className="csams-btn csams-btn--upload"
                disabled={!uploadAmsSelectedClient || uploadAmsLoading}
                onClick={onUploadToAMSConfirmed}
              >
                {uploadAmsLoading ? 'Uploading…' : 'Upload'}
              </button>
            </div>
          </div>
        </div>,
        document.body,
      )}

      {/* ── Preview Dialog ── */}
      {previewTx && (
        <DriverLicenseScanPreview
          tx={previewTx}
          onClose={() => setPreviewTx(null)}
          onDriverAdded={() => fetchData(page)}
          onUploadAms={() => openUploadAms(previewTx)}
        />
      )}
    </>
  );
};

export default DriverLicenseScanHistory;

